import React, { useState, useEffect, memo, lazy, Suspense, useMemo } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid, Typography } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import { setAddValue } from '../../../../redux/add-control-slice'
import { PatientService } from '../../../../services/patient.service'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import stylesheet from '../detail-container.stylesheet'
import CardLayoutComponent from '../common/card-layout.component'
import EmptyCardComponent from '../common/empty-card.component'
import Helper from '../../../../utils/helper'
import { toast } from 'react-toastify'

const SymptomReportDialogComponent = lazy(() => import('./symptom-report-dialog.component'))
const SymptomEvaluationDialogComponent = lazy(() => import('./symptom-evaluation-dialog.component'))

interface IProps {
  input: any
  classes: ClassNameMap
}

const SymptomEvaluationsComponent: React.FC<IProps> = ({ input, classes }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [evaluations, setEvaluations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<{ open: boolean; evaluation: any | null }>({ open: false, evaluation: null })

  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const addButtons = useSelector((state: RootState) => state.addButtons)
  const dispatch = useAppThunkDispatch()
  const urlParams: any = useParams()
  const patientId: string = urlParams.patientId || (patientDetail?.id ? String(patientDetail.id) : '')
  const patientService = useMemo(() => new PatientService(), [])
  const { updatePatientData } = useUpdateDataAction(patientId)

  // Listen for "Add new" click from section header
  useEffect(() => {
    const sxEvalBtn = addButtons?.addControl?.find((e: any) => e.key === 'MD-SXEVAL')
    if (sxEvalBtn?.value) {
      setAddDialogOpen(true)
      // Reset the flag
      const temp = addButtons.addControl.map((e: any) =>
        e.key === 'MD-SXEVAL' ? { ...e, value: false } : e
      )
      dispatch(setAddValue(temp))
    }
  }, [addButtons])

  // Get enrollment_id from program_enrollments
  const programEnrollments = patientDetail?.program_enrollments || []
  const activeEnrollmentId = programEnrollments[0]?.enrollment_id

  const refreshEvaluations = () =>
    patientService.getSymptomEvaluationsEnriched(patientId)
      .then((res: any) => setEvaluations(res?.data || []))
      .catch(() => {})

  // Fetch evaluations
  useEffect(() => {
    if (!patientId) return
    setLoading(true)
    patientService.getSymptomEvaluationsEnriched(patientId)
      .then((res: any) => setEvaluations(res?.data || []))
      .catch(() => setEvaluations([]))
      .finally(() => setLoading(false))
  }, [patientId, patientDetail?.medications])

  const openDetail = (evalId: number) => {
    const evaluation = evaluations.find((e: any) => e.id === evalId)
    if (evaluation) setDetail({ open: true, evaluation })
  }
  const closeDetail = () => setDetail({ open: false, evaluation: null })

  const handleResolve = (evalId: number) => {
    patientService.updateSymptomEvaluation(patientId, evalId, { phase: 'resolved' })
      .then(() => {
        setEvaluations(prev => prev.map(e => (e.id === evalId ? { ...e, phase: 'resolved' } : e)))
        closeDetail()
      })
      .catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
  }

  const handleReactivate = (evalId: number) => {
    patientService.updateSymptomEvaluation(patientId, evalId, { phase: 'active' })
      .then(() => {
        setEvaluations(prev => prev.map(e => (e.id === evalId ? { ...e, phase: 'active' } : e)))
        closeDetail()
      })
      .catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
  }

  // One accordion group (Active / Resolved) of symptom-evaluation cards. Same
  // machinery as Monitors/Lifestyle/Experiment: each row is a CardLayoutComponent
  // whose Edit opens the detail modal. In action-items mode the row is display-only.
  const renderGroup = (data: any[], label: string) => {
    if (!data || data.length === 0) return null
    return (
      <Accordion square defaultExpanded className={classes.patientAccrodionBg}>
        <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}>
          <Typography className={classes.subTitle}>{label}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container className={classes.zeroTopPadding}>
            <Grid item className={classes.fullWidth}>
              {data.map((d: any) => (
                <CardLayoutComponent
                  key={`sx-eval-${d.id}`}
                  id={d.id}
                  name={d.symptom_concept_name}
                  leftHeader={d.symptom_concept_name?.replace(/_/g, ' ')}
                  rightHeader={d.phase || ''}
                  subHeader={
                    (d.severity_grade ? `severity: ${d.severity_grade}/5` : '') +
                    (d.severity_grade && d.urgency ? ' • ' : '') +
                    (d.urgency || '')
                  }
                  bottomText={''}
                  needs_confirmation={false}
                  needs_update={d.phase === 'new'}
                  programs={[]}
                  // 'monitor' render path (like Experiment/Monitor): the Edit pencil
                  // lives in the click-anchored popover OUTSIDE the card, not inline.
                  // The 'more' entry is what renders that popover container.
                  icons={input?.isActionItems ? [] : [{ iconType: 'edit', callback: openDetail }, { iconType: 'more', callback: () => {} }]}
                  onRefClick={() => {}}
                  is_referred={false}
                  is_overridden={false}
                  entityRef='monitor'
                  patientId={patientId}
                  category={'medications'}
                />
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  const activeEvals = input?.isActionItems
    ? evaluations.filter((e: any) => e.phase === 'new')
    : evaluations.filter((e: any) => e.phase !== 'resolved')
  const completedEvals = evaluations.filter((e: any) => e.phase === 'resolved')

  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
      {loading && <CircularProgress size={20} sx={{ m: 2 }} />}

      {!loading && activeEvals.length > 0 && (
        <Grid item className={classes.fullWidth}>
          {renderGroup(activeEvals, 'Active')}
        </Grid>
      )}

      {!loading && completedEvals.length > 0 && !input?.actionProgram && !input?.isActionItems && (
        <Grid item className={classes.fullWidth}>
          {renderGroup(completedEvals, 'Resolved')}
        </Grid>
      )}

      {!loading && evaluations.length === 0 && !input?.actionProgram && (
        <Grid item className={classes.fullWidth}>
          <EmptyCardComponent />
        </Grid>
      )}

      {detail.open && detail.evaluation && (
        <Suspense fallback={null}>
          <SymptomEvaluationDialogComponent
            open={detail.open}
            handleClose={closeDetail}
            evaluation={detail.evaluation}
            patientId={patientId}
            patientDetail={patientDetail}
            onUpdate={refreshEvaluations}
            onActionApproved={(needsFullRefresh?: boolean) => {
              refreshEvaluations()
              if (needsFullRefresh) {
                updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
              }
            }}
            onResolve={handleResolve}
            onReactivate={handleReactivate}
          />
        </Suspense>
      )}

      {addDialogOpen && (
        <Suspense fallback={null}>
          <SymptomReportDialogComponent
            open={addDialogOpen}
            handleClose={() => {
              setAddDialogOpen(false)
              refreshEvaluations()
            }}
            patientId={patientId}
            enrollmentId={activeEnrollmentId}
          />
        </Suspense>
      )}
    </Grid>
  )
}

export default withStyles(stylesheet)(memo(SymptomEvaluationsComponent))
